import type { ReservationSort } from '@atomfit/shared';
import type { GymContentTranslations } from '@atomfit/shared';
import { apiClient } from './client.js';
import type {
  AccountCreditTransactionType,
  AccessCodeStatus,
  Booking,
  ReservationStatus,
  NotificationType,
  PaymentStatus,
  PricingTier,
  AppliedPromo,
} from '@atomfit/shared';
import type { PaginatedResult } from '../types/index.js';

export interface CreateBookingResponse {
  bookingId: string;
  bookingGroupId: string | null;
  bookingIds: string[];
  basePriceInSmallestUnit: number;
  priceInSmallestUnit: number;
  referralDiscountInSmallestUnit: number;
  promoDiscountInSmallestUnit: number;
  appliedPromo: AppliedPromo | null;
  creditAppliedInSmallestUnit: number;
  partnerPriceInSmallestUnit: number;
  currency: string;
  expiresAt: string;
  requiresPayment: boolean;
}

export interface BookingAccessCodeResponse {
  available: boolean;
  availableFrom: string;
  validUntil: string;
  code: string | null;
}

export interface OwnerBooking extends Booking {
  createdByAccountId?: string | null;
  createdBy?: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  account: {
    id: string;
    email: string;
    phone: string | null;
    firstName: string | null;
    lastName: string | null;
  };
  gym: {
    name: string;
    slug: string;
    tenant?: { id: string; name: string; slug: string; type: string };
  };
  slot: {
    startTime: string;
    endTime: string;
    priceInSmallestUnit: number;
    currency: string;
    pricingTier: PricingTier;
    capacityTotal: number;
    capacityBooked: number;
  };
  payment: {
    status: PaymentStatus;
    amountInSmallestUnit: number;
    refundedAmountInSmallestUnit: number;
    refundedAt: string | null;
    createdAt: string;
    updatedAt: string;
  } | null;
  accessCode: {
    status: AccessCodeStatus;
    validFrom: string;
    validUntil: string;
    attemptCount: number;
    lastAttemptAt: string | null;
    pinUsedAt: string | null;
    remoteOpenedAt: string | null;
    createdAt: string;
    code: string | null;
  } | null;
}

export interface ManagedBookingEvent {
  type: string;
  label: string;
  occurredAt: string;
  detail: string | null;
}

export interface ManagedBookingDetails extends OwnerBooking {
  events: ManagedBookingEvent[];
  notificationDeliveries: {
    id: string;
    type: NotificationType;
    channel: string;
    recipient: string;
    status: string;
    error: string | null;
    sentAt: string;
  }[];
}

export interface CustomerBooking extends Booking {
  gym: {
    id: string;
    name: string;
    slug: string;
    addressLine1: string;
    city: string;
    postcode: string;
    timezone: string;
    lat: number | null;
    lng: number | null;
    contentTranslations?: GymContentTranslations;
    equipmentDescription: string | null;
    directionsDescription: string | null;
    slotBufferMinutes: number;
    cancellationDeadlineHours: number;
    remoteOpenAvailable: boolean;
    tenant?: {
      name: string;
      slug: string;
      type: string;
    };
  };
  slot: {
    startTime: string;
    endTime: string;
    priceInSmallestUnit: number;
    currency: string;
    pricingTier: PricingTier;
    capacityTotal: number;
    capacityBooked: number;
  };
  accessCode: {
    status: AccessCodeStatus;
    validFrom: string;
    validUntil: string;
  } | null;
}

export interface BookingDetails extends Booking {
  conversionPayment: { status: PaymentStatus; amountInSmallestUnit: number; currency: string } | null;
  gym: {
    name: string;
    slug: string;
    addressLine1: string;
    city: string;
    postcode: string;
    timezone: string;
    slotBufferMinutes: number;
  };
  slot: {
    startTime: string;
    endTime: string;
    priceInSmallestUnit: number;
    currency: string;
    pricingTier: PricingTier;
    capacityTotal: number;
    capacityBooked: number;
  };
}

export interface CustomerCreditBalance {
  tenantId: string | null;
  tenantName: string;
  currency: string;
  balanceInSmallestUnit: number;
}

export interface CustomerCreditTransaction {
  id: string;
  tenantId: string | null;
  tenantName: string;
  amountInSmallestUnit: number;
  balanceAfterInSmallestUnit: number;
  currency: string;
  type: AccountCreditTransactionType;
  reason: string;
  bookingId: string | null;
  createdAt: string;
}

export interface CustomerCreditDashboard {
  balances: CustomerCreditBalance[];
  transactions: CustomerCreditTransaction[];
}

export const bookingsApi = {
  create: (
    gymId: string,
    slotStartTime: string,
    offerCode?: string,
    useCredit = true,
    includePartner = false,
    notes?: string,
  ) =>
    apiClient.post<CreateBookingResponse>('/bookings', {
      gymId,
      slotStartTime,
      offerCode,
      useCredit,
      includePartner,
      notes,
    }),

  createBatch: (
    gymId: string,
    slotStartTimes: string[],
    offerCode?: string,
    useCredit = true,
    includePartner = false,
    notes?: string,
  ) =>
    apiClient.post<CreateBookingResponse>('/bookings/batch', {
      gymId,
      slotStartTimes,
      offerCode,
      useCredit,
      includePartner,
      notes,
    }),

  replace: (
    bookingId: string,
    gymId: string,
    slotStartTime: string,
    offerCode?: string,
    useCredit = true,
    includePartner = false,
    notes?: string,
  ) =>
    apiClient.post<CreateBookingResponse>(`/bookings/${bookingId}/replace`, {
      gymId,
      slotStartTime,
      offerCode,
      useCredit,
      includePartner,
      notes,
    }),

  list: () => apiClient.get<{ data: Booking[] }>('/bookings'),

  my: () => apiClient.get<{ data: CustomerBooking[] }>('/bookings/my'),

  myCredits: () => apiClient.get<CustomerCreditDashboard>('/bookings/my/credits'),

  get: (bookingId: string) => apiClient.get<BookingDetails>(`/bookings/${bookingId}`),

  accessCode: (bookingId: string) =>
    apiClient.get<BookingAccessCodeResponse>(`/bookings/${bookingId}/access-code`),

  remoteOpen: (bookingId: string) =>
    apiClient.post<{ opened: boolean }>(`/bookings/${bookingId}/remote-open`),

  cancel: (bookingId: string, reason?: string) =>
    apiClient.post(`/bookings/${bookingId}/cancel`, { reason }),

  ownerList: (
    params: {
      sortBy?: ReservationSort;
      sortDirection?: 'asc' | 'desc';
      createdRange?: string;
      createdFrom?: string;
      createdTo?: string;
      creator?: string;
      createdByAccountId?: string;
      metric?: string;
      metricFrom?: string;
      metricTo?: string;
      status?: ReservationStatus;
      gymId?: string;
      accountId?: string;
      q?: string;
      page?: number;
      range?: string;
      from?: string;
      to?: string;
    } = {},
  ) => apiClient.get<PaginatedResult<OwnerBooking>>('/owner/bookings', { params }),

  adminList: (
    params: {
      sortBy?: ReservationSort;
      sortDirection?: 'asc' | 'desc';
      createdRange?: string;
      createdFrom?: string;
      createdTo?: string;
      creator?: string;
      createdByAccountId?: string;
      metric?: string;
      metricFrom?: string;
      metricTo?: string;
      status?: ReservationStatus;
      gymId?: string;
      accountId?: string;
      tenantSlug?: string;
      q?: string;
      page?: number;
      range?: string;
      from?: string;
      to?: string;
    } = {},
  ) => apiClient.get<PaginatedResult<OwnerBooking>>('/admin/bookings', { params }),

  adminGet: (role: string, bookingId: string) =>
    apiClient.get<ManagedBookingDetails>(
      `${role === 'PLATFORM_ADMIN' ? '/admin' : '/owner'}/bookings/${bookingId}`,
    ),

  adminCreateManual: (
    role: string,
    data: {
      gymId: string;
      slotStartTime: string;
      email: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      priceInSmallestUnit?: number;
      currency?: string;
      paymentStatus?: 'PENDING' | 'PAID';
      notes?: string;
    },
  ) =>
    apiClient.post<ManagedBookingDetails>(
      `${role === 'PLATFORM_ADMIN' ? '/admin' : '/owner'}/bookings/manual`,
      data,
    ),

  ownerCancel: (
    bookingId: string,
    reason?: string,
    refundMode: 'CREDIT' | 'CASH' | 'NONE' = 'NONE',
  ) => apiClient.post(`/owner/bookings/${bookingId}/cancel`, { reason, refundMode }),

  adminUpdateStatus: (role: string, bookingId: string, status: ReservationStatus) =>
    apiClient.patch(
      `${role === 'PLATFORM_ADMIN' ? '/admin' : '/owner'}/bookings/${bookingId}/status`,
      { status },
    ),

  adminDelete: (role: string, bookingId: string) =>
    apiClient.delete(`${role === 'PLATFORM_ADMIN' ? '/admin' : '/owner'}/bookings/${bookingId}`),

  adminCancel: (
    role: string,
    bookingId: string,
    data: { reason?: string; refundMode: 'CREDIT' | 'CASH' | 'NONE' },
  ) =>
    apiClient.post(
      `${role === 'PLATFORM_ADMIN' ? '/admin' : '/owner'}/bookings/${bookingId}/cancel`,
      data,
    ),

  adminExportCsv: (
    role: string,
    params: {
      sortBy?: ReservationSort;
      sortDirection?: 'asc' | 'desc';
      createdRange?: string;
      createdFrom?: string;
      createdTo?: string;
      creator?: string;
      createdByAccountId?: string;
      metric?: string;
      metricFrom?: string;
      metricTo?: string;
      status?: ReservationStatus;
      gymId?: string;
      accountId?: string;
      tenantSlug?: string;
      q?: string;
      range?: string;
      from?: string;
      to?: string;
    } = {},
  ) =>
    apiClient.get<Blob>(`${role === 'PLATFORM_ADMIN' ? '/admin' : '/owner'}/bookings/export.csv`, {
      params,
      responseType: 'blob',
    }),
};
